import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import toast from 'react-hot-toast';

const ROLE_LABEL = { student: 'Student', lecturer: 'Supervising Lecturer', alumni: 'Alumni Sponsor', judge: 'Industry Judge', admin: 'Programme Administrator' };
const ROLE_COLOR = { student: 'var(--teal)', lecturer: 'var(--gold)', alumni: 'var(--gold)', judge: 'var(--teal)', admin: 'var(--teal)' };

export default function CertificatesPage() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    api.get('/certificates/my')
      .then(r => setCerts(r.data))
      .catch(() => toast.error('Failed to load certificates'))
      .finally(() => setLoading(false));
  }, []);

  const download = async (certId) => {
    try {
      const res = await api.get('/certificates/' + certId + '/download', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = certId + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const bulkIssue = async () => {
    if (!window.confirm('Issue certificates to all eligible participants now?')) return;
    setIssuing(true);
    try {
      const res = await api.post('/admin/certificates/bulk-issue');
      toast.success(res.data.issued + ' certificate(s) issued');
      const r = await api.get('/certificates/my');
      setCerts(r.data);
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'Bulk issue failed');
    } finally { setIssuing(false); }
  };

  // Only the top three have a placing; finalists' ranks are not printed.
  const rankLabel = (rank) => ['1st Place', '2nd Place', '3rd Place'][rank - 1] || null;

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', color: 'var(--text-primary)', margin: 0 }}>
            My Certificates
          </h1>
          <p style={{ color: 'var(--text-muted)', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            Issued upon reaching the Futurum Innovation Awards Finale
          </p>
        </div>
        {user && user.role === 'admin' && (
          <button onClick={bulkIssue} disabled={issuing} style={{ background: 'var(--gold)', color: '#0D1117', border: 'none', borderRadius: 8, padding: '0.65rem 1.4rem', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.875rem', cursor: issuing ? 'not-allowed' : 'pointer', opacity: issuing ? 0.7 : 1 }}>
            {issuing ? 'Issuing...' : 'Bulk Issue Certificates'}
          </button>
        )}
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}

      {!loading && certs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'var(--ink-mid)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎓</div>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>No certificates yet</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Certificates are issued automatically when your project reaches the Finale.</p>
        </div>
      )}

      {!loading && certs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {certs.map(function(cert) {
            return (
              <div key={cert.id} style={{ background: 'var(--ink-mid)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 12, padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: '2px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                    🏅
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ background: ROLE_COLOR[cert.role], color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: 'var(--font-mono)' }}>
                        {(cert.metadata && cert.metadata.caption) || ROLE_LABEL[cert.role] || cert.role}
                      </span>
                      {rankLabel(cert.rank) && <span style={{ color: 'var(--gold)', fontSize: '0.8rem', fontWeight: 700 }}>{rankLabel(cert.rank)}</span>}
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem' }}>
                      {cert.metadata && cert.metadata.project_title ? '"' + cert.metadata.project_title + '"' : cert.season + ' — ' + (ROLE_LABEL[cert.role] || cert.role)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem', fontFamily: 'var(--font-mono)' }}>
                      {cert.cert_id} · Issued {new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <a href={'/verify/' + cert.cert_id} target='_blank' rel='noreferrer' style={{ color: 'var(--teal)', fontSize: '0.8rem', textDecoration: 'none', fontFamily: 'var(--font-mono)' }}>Verify</a>
                  <button onClick={function() { download(cert.cert_id); }} style={{ background: 'var(--gold)', color: '#0D1117', border: 'none', borderRadius: 8, padding: '0.5rem 1.2rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Download PDF</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}